import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import type {
  CreateTrainingInterviewResponse,
  TrainingCandidateInterviewInfoResponse,
  TrainingInterviewCreateRequest,
  TrainingInterviewCreationResponse,
} from '@/services/api';
import { TrainingService } from '@/services/api';

export const coachInterviewKeys = {
  coachInterviewTrainingQueryBase: () =>
    ['coachInterviewTrainingQueryBase'] as const,
  coachInterviewTrainingQuery: (params: { interviewsId: string }) =>
    [...coachInterviewKeys.coachInterviewTrainingQueryBase(), params] as const,
};

export const useCoachInterviewInfoQuery = (
  params: {
    interviewsId: string;
    securityToken: string;
  },
  options?: Partial<
    UseQueryOptions<TrainingCandidateInterviewInfoResponse, AxiosError>
  >
) => {
  return useQuery({
    queryKey: coachInterviewKeys.coachInterviewTrainingQuery(params),
    queryFn: () =>
      TrainingService.getAllTrainingInterviewInfo(params, {
        overrideSecurityToken: params.securityToken,
      }),

    ...options,
  });
};

export const useNewCoachMutation = (
  options?: Partial<
    UseMutationOptions<
      CreateTrainingInterviewResponse,
      AxiosError,
      TrainingInterviewCreateRequest
    >
  >
) => {
  return useMutation<
    CreateTrainingInterviewResponse,
    AxiosError,
    TrainingInterviewCreateRequest
  >({
    mutationFn: async (body) => {
      const requestId = await TrainingService.postTrainingInterview({
        body,
      });

      let pollResponse: TrainingInterviewCreationResponse;
      do {
        pollResponse = await TrainingService.getTrainingInterviewStatus({
          requestId,
        });

        if (pollResponse.isWaiting) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      } while (pollResponse.isWaiting);

      return pollResponse.data!;
    },
    ...options,
  });
};
